import { z } from 'zod';
import { CurrencyCode, MinorUnits, SignedMinorUnits } from './money';
import { GameRoundResult } from './result';

/** Operator-facing API. Every request is HMAC signed and IP allow-listed. */

export const GameKind = z.enum(['INSTANT', 'LOBBY', 'PEER']);
export type GameKind = z.infer<typeof GameKind>;

export const GameType = z.enum([
  'SLOT',
  'DICE',
  'TABLE',
  'CRASH',
  'FISH',
  'WHEEL',
  'PLINKO',
  'SPACE',
  'SUMMIT',
  'MINES',
  'KENO',
]);
export type GameType = z.infer<typeof GameType>;

export const Platform = z.enum(['DESKTOP', 'MOBILE']);
export type Platform = z.infer<typeof Platform>;

export const PlayMode = z.enum(['REAL', 'DEMO']);
export type PlayMode = z.infer<typeof PlayMode>;

export const LaunchRequest = z.object({
  /** The operator's player identifier. We never require a real name. */
  playerId: z.string().min(1).max(128),
  /** Optional display nickname shown in the game UI. */
  playerName: z.string().max(64).optional(),
  currency: CurrencyCode,
  gameCode: z.string().min(1).max(64),
  language: z.string().min(2).max(5).default('en'),
  mode: PlayMode.default('REAL'),
  platform: Platform.default('DESKTOP'),
  /** Player's IP, forwarded for the operator's own risk rules and our logs. */
  playerIp: z.string().max(64).optional(),
  /** Where the game's "back to lobby" button should send the player. */
  lobbyUrl: z.url().optional(),
  depositUrl: z.url().optional(),
  /**
   * Idle session timeout in seconds. The session's `expiresAt` slides forward
   * on each authenticated request by this window, up to an absolute lifetime
   * cap. Clamped server-side to a sane range.
   */
  sessionTimeoutSeconds: z.number().int().min(60).max(86400).optional(),
});
export type LaunchRequest = z.infer<typeof LaunchRequest>;

export const LaunchResponse = z.object({
  status: z.literal('OK'),
  /** Ready-to-embed URL; the operator should open it in an iframe or tab. */
  launchUrl: z.url(),
  /** Single-use, short-lived. Exchanged by the client for a session token. */
  launchToken: z.string(),
  sessionId: z.uuid(),
  expiresAt: z.iso.datetime(),
});
export type LaunchResponse = z.infer<typeof LaunchResponse>;

export const BetLimits = z.object({
  currency: CurrencyCode,
  minBet: MinorUnits,
  maxBet: MinorUnits,
  /** Allowed bet-per-line ladder, in minor units. */
  betLevels: z.array(MinorUnits).min(1),
  defaultBetLevel: MinorUnits,
});
export type BetLimits = z.infer<typeof BetLimits>;

export const GameSummary = z.object({
  gameCode: z.string(),
  name: z.string(),
  type: GameType,
  kind: GameKind,
  /** Theoretical return to player as certified, e.g. 0.962. */
  rtp: z.number(),
  volatility: z.enum(['LOW', 'MEDIUM', 'HIGH']),
  lines: z.number().int().positive(),
  reels: z.number().int().positive(),
  rows: z.number().int().positive(),
  features: z.array(z.string()),
  maxWinMultiplier: z.number(),
  thumbnailUrl: z.string().optional(),
  supportedLanguages: z.array(z.string()),
  supportedPlatforms: z.array(Platform),
  supportedCurrencies: z.array(CurrencyCode),
  demoAvailable: z.boolean(),
  tags: z.array(z.string()),
  isNew: z.boolean(),
  isHot: z.boolean(),
  sortOrder: z.number().int(),
  enabled: z.boolean(),
  limits: z.array(BetLimits),
});
export type GameSummary = z.infer<typeof GameSummary>;

export const GameListResponse = z.object({
  status: z.literal('OK'),
  games: z.array(GameSummary),
});
export type GameListResponse = z.infer<typeof GameListResponse>;

export const RoundState = z.enum([
  'BET_PENDING',
  'IN_PLAY',
  'RESOLVED',
  'SETTLE_PENDING',
  'CLOSED',
  'ROLLBACK_PENDING',
  'ROLLED_BACK',
  'FAILED',
]);
export type RoundState = z.infer<typeof RoundState>;

/** States a background worker still owes an action for. */
export const RECOVERABLE_ROUND_STATES: readonly RoundState[] = [
  'SETTLE_PENDING',
  'ROLLBACK_PENDING',
];

export const RoundSummary = z.object({
  roundId: z.uuid(),
  playerId: z.string(),
  gameCode: z.string(),
  currency: CurrencyCode,
  /** DEMO rounds moved play money and are excluded from revenue reporting. */
  mode: PlayMode,
  betAmount: MinorUnits,
  winAmount: MinorUnits,
  state: RoundState,
  startedAt: z.iso.datetime(),
  closedAt: z.iso.datetime().nullable(),
});
export type RoundSummary = z.infer<typeof RoundSummary>;

export const TransactionType = z.enum(['BET', 'WIN', 'ROLLBACK']);
export type TransactionType = z.infer<typeof TransactionType>;

export const TransactionRecord = z.object({
  txId: z.uuid(),
  type: TransactionType,
  amount: MinorUnits,
  currency: CurrencyCode,
  status: z.enum(['PENDING', 'CONFIRMED', 'FAILED', 'ROLLED_BACK']),
  operatorTxId: z.string().nullable(),
  balanceAfter: MinorUnits.nullable(),
  attempts: z.number().int().min(0),
  createdAt: z.iso.datetime(),
  confirmedAt: z.iso.datetime().nullable(),
});
export type TransactionRecord = z.infer<typeof TransactionRecord>;

export const RoundDetail = RoundSummary.extend({
  sessionId: z.uuid(),
  mathVersion: z.string(),
  /** Hex seed the round was generated from. Replaying it reproduces `result`. */
  rngSeed: z.string(),
  result: GameRoundResult.nullable(),
  transactions: z.array(TransactionRecord),
});
export type RoundDetail = z.infer<typeof RoundDetail>;

export const RoundListQuery = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  playerId: z.string().optional(),
  gameCode: z.string().optional(),
  state: RoundState.optional(),
  mode: PlayMode.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(100),
});
export type RoundListQuery = z.infer<typeof RoundListQuery>;

export const RoundListResponse = z.object({
  status: z.literal('OK'),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  rounds: z.array(RoundSummary),
});
export type RoundListResponse = z.infer<typeof RoundListResponse>;

// ---------------------------------------------------------------------------
// Operational control. The endpoints an integrator needs on day one when a
// player complains, a round hangs, or a session has to be ended.
// ---------------------------------------------------------------------------

export const KickPlayerResponse = z.object({
  status: z.literal('OK'),
  playerId: z.string(),
  /** Sessions that were open and have now been closed. */
  sessionsClosed: z.number().int().min(0),
});
export type KickPlayerResponse = z.infer<typeof KickPlayerResponse>;

/**
 * Lookup by whichever identifier the caller happens to hold: ours, theirs, or
 * the round the transaction belongs to. At least one is required, because an
 * unfiltered dump of the ledger is not a support tool.
 */
export const TransactionLookupQuery = z
  .object({
    txId: z.uuid().optional(),
    roundId: z.uuid().optional(),
    operatorTxId: z.string().min(1).max(128).optional(),
    playerId: z.string().min(1).max(128).optional(),
  })
  .refine((query) => Boolean(query.txId ?? query.roundId ?? query.operatorTxId ?? query.playerId), {
    message: 'one of txId, roundId, operatorTxId or playerId is required',
  });
export type TransactionLookupQuery = z.infer<typeof TransactionLookupQuery>;

export const TransactionDetail = TransactionRecord.extend({
  roundId: z.uuid(),
  playerId: z.string(),
  gameCode: z.string(),
  refTxId: z.uuid().nullable(),
  lastError: z.string().nullable(),
});
export type TransactionDetail = z.infer<typeof TransactionDetail>;

export const TransactionLookupResponse = z.object({
  status: z.literal('OK'),
  transactions: z.array(TransactionDetail),
});
export type TransactionLookupResponse = z.infer<typeof TransactionLookupResponse>;

export const UnfinishedRound = RoundSummary.extend({
  /** How many times a worker has already tried to finish this round. */
  attempts: z.number().int().min(0),
  lastError: z.string().nullable(),
  nextActionAt: z.iso.datetime().nullable(),
  /** False when the round owes the player a payout, which is never cancelled. */
  cancellable: z.boolean(),
});
export type UnfinishedRound = z.infer<typeof UnfinishedRound>;

export const UnfinishedRoundsResponse = z.object({
  status: z.literal('OK'),
  rounds: z.array(UnfinishedRound),
});
export type UnfinishedRoundsResponse = z.infer<typeof UnfinishedRoundsResponse>;

/** The body is optional in full: a cancel with no explanation is still a cancel. */
export const CancelRoundRequest = z
  .object({
    reason: z.string().min(1).max(256).optional(),
  })
  .default({});
export type CancelRoundRequest = z.infer<typeof CancelRoundRequest>;

export const CancelRoundResponse = z.object({
  status: z.literal('OK'),
  roundId: z.uuid(),
  state: RoundState,
  /** True when the stake still has to be returned by the recovery worker. */
  refundPending: z.boolean(),
});
export type CancelRoundResponse = z.infer<typeof CancelRoundResponse>;

export const GgrQuery = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  gameCode: z.string().optional(),
  currency: CurrencyCode.optional(),
  groupBy: z.enum(['DAY', 'GAME', 'CURRENCY', 'DAY_GAME']).default('DAY'),
});
export type GgrQuery = z.infer<typeof GgrQuery>;

export const GgrRow = z.object({
  date: z.iso.date().optional(),
  gameCode: z.string().optional(),
  currency: CurrencyCode,
  rounds: z.number().int(),
  players: z.number().int(),
  turnover: MinorUnits,
  payout: MinorUnits,
  /** turnover - payout. Negative on a bad day; this is why it is signed. */
  ggr: SignedMinorUnits,
  /** Realised RTP for the slice, useful for spotting a broken math model. */
  rtp: z.number(),
});
export type GgrRow = z.infer<typeof GgrRow>;

export const GgrResponse = z.object({
  status: z.literal('OK'),
  rows: z.array(GgrRow),
});
export type GgrResponse = z.infer<typeof GgrResponse>;
